/**
 * GamesAdapter -- Bridges the Games hub into the AWENI shell.
 *
 * Loads the games hub via iframe with communication bridge for auth,
 * theme, locale, and game events (start, complete, navigate).
 */

import type { AweniContext, AweniModule, IframeMessage } from '../../types';

export interface GameSession {
  gameKey: string;
  score: number;
  duration: number;
  completedAt: string;
}

export interface GameCatalogItem {
  key: string;
  name: string;
  category: string;
  thumbnail: string;
  playCount: number;
}

const OUTBOUND = {
  AUTH_STATE: 'AWENI_AUTH_STATE',
  THEME: 'AWENI_THEME',
  LOCALE: 'AWENI_LOCALE',
} as const;

const INBOUND = {
  GAME_STARTED: 'AWENI_GAME_STARTED',
  GAME_COMPLETED: 'AWENI_GAME_COMPLETED',
  NAVIGATE: 'AWENI_NAVIGATE',
  READY: 'AWENI_READY',
} as const;

export default class GamesAdapter implements AweniModule {
  private container: HTMLElement | null = null;
  private context: AweniContext | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private messageHandler: ((e: MessageEvent) => void) | null = null;
  private resizeObserver: ResizeObserver | null = null;

  async mount(container: HTMLElement, ctx: AweniContext): Promise<void> {
    this.container = container;
    this.context = ctx;
    const t = ctx.i18n.t.bind(ctx.i18n);

    container.innerHTML = `
      <div class="games-adapter relative w-full h-full" style="min-height: calc(100vh - 120px)">
        <div class="games-skeleton absolute inset-0 flex flex-col items-center justify-center gap-4" style="background: var(--bg-secondary)">
          <div class="w-16 h-16 rounded-2xl animate-pulse" style="background: #3B82F640"></div>
          <div class="flex flex-col items-center gap-2">
            <div class="h-4 w-32 rounded animate-pulse" style="background: var(--border)"></div>
            <p class="text-sm" style="color: var(--text-secondary)">${t('games_loading')}</p>
          </div>
          <div class="flex gap-3 mt-4">
            <div class="h-8 w-20 rounded-lg animate-pulse" style="background: var(--border)"></div>
            <div class="h-8 w-20 rounded-lg animate-pulse" style="background: var(--border)"></div>
            <div class="h-8 w-20 rounded-lg animate-pulse" style="background: var(--border)"></div>
          </div>
        </div>
        <iframe
          id="games-iframe"
          class="w-full h-full border-0 opacity-0 transition-opacity duration-300"
          style="min-height: calc(100vh - 120px)"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          loading="lazy"
          title="${t('games_title')}"
          aria-label="${t('games_title')}"
        ></iframe>
      </div>
    `;

    this.iframe = container.querySelector<HTMLIFrameElement>('#games-iframe');
    if (!this.iframe) return;

    // Set up message listener
    this.messageHandler = (e: MessageEvent) => this.handleMessage(e);
    window.addEventListener('message', this.messageHandler);

    // Resize observer
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (this.iframe) {
          this.iframe.style.height = `${entry.contentRect.height}px`;
        }
      }
    });
    this.resizeObserver.observe(container);

    // Handle iframe load
    this.iframe.addEventListener('load', () => {
      const skeleton = container.querySelector('.games-skeleton');
      if (skeleton) skeleton.remove();
      if (this.iframe) this.iframe.classList.remove('opacity-0');
      this.sendState();
    });

    // Handle iframe error
    this.iframe.addEventListener('error', () => {
      const skeleton = container.querySelector('.games-skeleton');
      if (skeleton) {
        skeleton.innerHTML = `
          <div class="text-center p-6">
            <svg class="w-12 h-12 mx-auto mb-3" style="color: var(--text-secondary)" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/>
            </svg>
            <p class="text-sm" style="color: var(--text-secondary)">${t('games_error')}</p>
          </div>
        `;
      }
    });

    this.iframe.src = '/Projects/Games/hub/';
  }

  unmount(): void {
    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.iframe) {
      this.iframe.src = 'about:blank';
      this.iframe.remove();
      this.iframe = null;
    }
    if (this.container) {
      this.container.innerHTML = '';
    }
    this.container = null;
    this.context = null;
  }

  private sendState(): void {
    if (!this.context) return;
    this.sendToIframe(OUTBOUND.AUTH_STATE, {
      token: this.context.auth.getToken(),
      isAuthenticated: this.context.auth.isAuthenticated(),
    });
    this.sendToIframe(OUTBOUND.THEME, {
      mode: this.context.theme.mode,
      isDark: this.context.theme.isDark,
    });
    this.sendToIframe(OUTBOUND.LOCALE, {
      locale: this.context.i18n.locale,
      dir: this.context.i18n.dir,
    });
  }

  private sendToIframe(type: string, payload: Record<string, unknown>): void {
    if (!this.iframe?.contentWindow) return;
    try {
      this.iframe.contentWindow.postMessage({ type, payload } as IframeMessage, '*');
    } catch {
      // Iframe not ready
    }
  }

  private handleMessage(event: MessageEvent): void {
    if (!event.data || typeof event.data.type !== 'string') return;
    const { type, payload } = event.data as IframeMessage;

    switch (type) {
      case INBOUND.READY:
        this.sendState();
        break;
      case INBOUND.GAME_STARTED:
        this.onGameStarted(payload);
        break;
      case INBOUND.GAME_COMPLETED:
        this.onGameCompleted(payload);
        break;
      case INBOUND.NAVIGATE:
        if (payload.module && this.context) {
          this.context.navigate(payload.module as string, payload.path as string | undefined);
        }
        break;
    }
  }

  private onGameStarted(payload: Record<string, unknown>): void {
    const gameKey = (payload.gameKey as string) || 'unknown';
    const gameName = (payload.gameName as string) || gameKey;
    try {
      this.context?.api.post('/aweni/api/v1/activity', {
        app: 'games',
        action: 'game_started',
        label: gameName,
        meta: { gameKey },
      });
    } catch {
      // Activity tracking non-critical
    }
  }

  private async onGameCompleted(payload: Record<string, unknown>): Promise<void> {
    const gameKey = (payload.gameKey as string) || 'unknown';
    const score = (payload.score as number) || 0;
    const gameName = (payload.gameName as string) || gameKey;

    // Save to IndexedDB for offline sync
    try {
      const db = await this.openDB();
      const tx = db.transaction('game_sessions', 'readwrite');
      tx.objectStore('game_sessions').put({
        id: crypto.randomUUID(),
        gameKey,
        score,
        timestamp: new Date().toISOString(),
        synced: false,
      });
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } catch {
      // IndexedDB not available
    }

    // Queue sync via API
    try {
      await this.context?.api.post('/aweni/api/v1/activity', {
        app: 'games',
        action: 'game_completed',
        label: gameName,
        meta: { gameKey, score },
      });
    } catch {
      // Will be retried via sync engine
    }

    if (this.context) {
      this.context.showNotification(
        this.context.i18n.t('games_title') + ' - ' + gameName,
        'success',
      );
    }
  }

  private openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('aweni-offline-v1', 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains('game_sessions')) {
          db.createObjectStore('game_sessions', { keyPath: 'id' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
}
